import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { SyncFilesService } from '../services/sync-files.service';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash';
import { BzgToastService } from 'bz-genome-angular';

@Component({
  selector: 'biz-export-sync-files',
  templateUrl: './export-sync-files.component.html',
  styleUrls: ['./export-sync-files.component.scss']
})
export class ExportSyncFilesComponent implements OnInit {
  @ViewChild("fileLink") fileLink: ElementRef;
  @Output() onFinish = new EventEmitter();

  nodes = Array<IItemTreeView>();
  selectedNodes = Array<IItemTreeView>();
  completeNodes = Array<IItemTreeView>();
  filterValue: string;
  data: any;
  status: string;
  fileName: string;
  isLoading = false;
  messageStatus: string;
  filesWithError: any[];

  constructor(private _syncFilesService: SyncFilesService,
    private _toastService: BzgToastService,
    private _translateService: TranslateService) { }

  ngOnInit() {
    this.loadAttributesData();
  }

  public closeModal() {
    this.onFinish.emit();
  }


  onSelectedNode(node: IItemTreeView) {
    this.selectedNodes = this.getSelectedNodes(this.nodes);
    this.updateCompleteNodes(this.nodes, this.completeNodes);
  }

  onFilterValueChange(search: any) {
    this.nodes = cloneDeep(this.completeNodes);
    this.filterNodes(this.nodes, this.filterValue);
  }

  async onExport() {
    this.showLoading('exporting');    
    this.updateDataWithSelectedValues(this.nodes);
    var selectedEntities = this.createGuidHashSet();
    var response = await this._syncFilesService.exportFiles(selectedEntities).catch(error => {
      this.hideLoading();
      this.closeModal();
    });
    response = JSON.parse(response);
    if (response.Issues && Object.keys(response.Issues).length > 0) {
      this.filesWithError = await this.extractFilesWithError(response);
    }

    if (!this.filesWithError && response.File) {
      this.status = 'success';
      this.fileName = response.File;
    } else {
      if(response.ErrorLog){                
        this.closeModal();
        this.ShowError(response.ErrorLog);
      }else{
        this.status = 'error';
      }
    }

    this.hideLoading();
  }

  onDownloadFile() {
    this._syncFilesService.downloadFileExport(this.fileName).then(data => {
      let file = new Blob([data], { type: "application/octet-stream" });
      this.fileLink.nativeElement.href = URL.createObjectURL(file);
      this.fileLink.nativeElement.download = this.fileName;
      this.fileLink.nativeElement.click();
    });
  }

  private async extractFilesWithError(response: any) {
    let filesWithError = [];

    for (let entityKey in response.Issues) {
      let issues = response.Issues[entityKey];
      for (let issue of issues) {
        let errorMessage = await this.getErrorMessage(issue.IssueType);
        filesWithError.push({
          EntitySource: issue.EntityDisplayName,
          AttributeDisplayName: issue.AttributeDisplayName,
          FileName: issue.FileName,
          IdEntityValue: issue.IdEntityValue,
          Error: errorMessage,
        });
      }  
    }

    return filesWithError;
  }

  private createGuidHashSet() {
    let guidHashSet = new Set<string>();
    for (let key in this.data.Entities) {
      let entity = this.data.Entities[key];
      if (entity.Selected) {
        guidHashSet.add(entity.EntityGuid);
      }
    }
    return guidHashSet;
  }

  private getErrorMessage(errorCode: number): Promise<string> {
    return new Promise(resolve => {
      this._translateService.get(`SYNC_FILES.ERROR.${errorCode}`).subscribe((message: string) => {
        resolve(message);
      });
    });
  }

  private updateDataWithSelectedValues(nodes: IItemTreeView[]) {
    nodes.forEach(node => {
      if (node.isChecked) {
        let entity = this.data.Entities[node.guid.toString()];
        if (entity) {
          entity.Selected = true;
        }
      }
      if (node.nodes) {
        this.updateDataWithSelectedValues(node.nodes);
      }
    });
  }

  private updateCompleteNodes(updatedNodes: IItemTreeView[], originalNodes: IItemTreeView[]) {
    updatedNodes.forEach(node => {
      const originalNode = originalNodes.find(n => n.guid === node.guid);
      if (originalNode) {
        originalNode.isChecked = node.isChecked;
        originalNode.isPartial = node.isPartial;
        originalNode.isExpanded = node.isExpanded;
      }

      if (node.nodes && originalNode.nodes) {
        this.updateCompleteNodes(node.nodes, originalNode.nodes);
      }
    });
  }

  isSomethingSelected() {
    return this.selectedNodes.length === 0;
  }

  private getSelectedNodes(nodes: IItemTreeView[]): IItemTreeView[] {
    let selectedNodes: IItemTreeView[] = [];

    nodes.forEach(node => {
      if (node.isChecked) {
        selectedNodes.push(node);
      }

      if (node.nodes) {
        selectedNodes = [...selectedNodes, ...this.getSelectedNodes(node.nodes)];
      }
    });
    return selectedNodes;
  }

  private async loadAttributesData() {
    this.showLoading('loading');
    var response = await this._syncFilesService.getAttributes();
    this.data = response.SyncDataContent;
    if (this.data.Entities && Object.keys(this.data.Entities).length > 0) {
      const grouped = this.mapDataToTreeView();
      const treeViewList: IItemTreeView[] = Object.keys(grouped).map(key => grouped[key]);
      this.createRootNode(treeViewList);
    } else {
      this.status = 'nodata';
    }

    this.hideLoading();
  }

  private mapDataToTreeView() {
    let result = [];
    for (let key in this.data.Entities) {
      let item = this.data.Entities[key];
      result.push({
        guid: item.EntityGuid,
        icon: 'icon-entity-enum',
        displayName: item.EntityDisplayName,
        hasCheckbox: true,
        isEnable: true,
        nodes: item.Attributes.map(attribute => {
          return {
            guid: attribute,
            displayName: attribute,
            icon: 'icon-attribute-upload',
            hasCheckbox: true,
            isEnable: false,
            nodes: []
          };
        })
      });
    }
    result.sort((a, b) => a.displayName.localeCompare(b.displayName));
    return result;
  }

  private createRootNode(treeViewList: IItemTreeView[]) {
    let rootNode: IItemTreeView = {
      guid: 'root',
      displayName: this._translateService.instant('SYNC_FILES.ENTITIES'),
      icon: 'icon-entity',
      isSelected: false,
      hasCheckbox: true,
      isExpanded: true,
      isEnable:true,
      nodes: treeViewList
    };
    this.nodes = [rootNode];
    this.completeNodes = cloneDeep(this.nodes);
  }

  private filterNodes(nodes: IItemTreeView[], filterValue: string): IItemTreeView[] {
    return nodes.filter(node => {
      if (node.displayName.toLowerCase().includes(filterValue.toLowerCase())) {
        return true;
      }

      if (node.nodes) {
        node.nodes = this.filterNodes(node.nodes, filterValue);
        return node.nodes.length > 0;
      }

      return false;
    });
  }

  private showLoading(status: string) {
    this.messageStatus = status;
    this.isLoading = true;
  }

  private ShowError(message:string) {
    this._translateService.get(`SYNC_FILES.${message}`).subscribe((message: string) => {
      this._toastService.open(
        message,
        {
          type: 'error',
          duration: 0,
          verticalPosition: "bottom",
          horizontalPosition: "right"
        });
    });
  }

  private hideLoading() {
    this.messageStatus = '';
    this.isLoading = false;
  }
}
